import React, { useEffect, useState } from 'react'
import { Alert, Checkbox, Prompt, TextTooltip } from '@root/components'
import { useTranslation } from 'react-i18next'
import { IInvoice, updateInvoicePostDueAutomation } from '@root/query'
import { useBusinessContext, useFeature } from '@root/context'
import { useMutation } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { FaQuestionCircle, FaSave } from 'react-icons/fa'
import styled from 'styled-components'
import { isEligibleForPostDueAutomation } from '@root/utils'
import { showKraviaDebtCollectionCheckbox } from './postDueUi'
import { useInvalidateInvoiceQueries } from './useInvalidateInvoiceQueries'

interface Props {
  invoice: IInvoice
  handleOnClose: () => void
  showPrompt: boolean
}

const InvoicePostDueAutomationSettings: React.FC<Props> = ({
  invoice,
  handleOnClose,
  showPrompt
}) => {
  const [t] = useTranslation()
  const { slug, data: business } = useBusinessContext()
  const { enabled: kraviaEnabled } = useFeature('kravia')
  const invalidateInvoiceQueries = useInvalidateInvoiceQueries(invoice.id)

  const [softReminderEnabled, setSoftReminderEnabled] = useState(
    invoice.is_automatic_soft_reminder_enabled
  )
  const [debtCollectionEnabled, setDebtCollectionEnabled] = useState(
    invoice.is_automatic_debt_collection_enabled
  )

  useEffect(() => {
    if (!showPrompt) return
    setSoftReminderEnabled(invoice.is_automatic_soft_reminder_enabled)
    setDebtCollectionEnabled(invoice.is_automatic_debt_collection_enabled)
  }, [
    showPrompt,
    invoice.id,
    invoice.is_automatic_soft_reminder_enabled,
    invoice.is_automatic_debt_collection_enabled
  ])

  const cannotUsePostDueAutomation = !isEligibleForPostDueAutomation(invoice)
  const showKraviaCheckbox = showKraviaDebtCollectionCheckbox({
    kraviaEnabled,
    business,
    invoiceDebtCollectionEnabled: invoice.is_automatic_debt_collection_enabled
  })

  const updateMutation = useMutation({
    mutationFn: () =>
      updateInvoicePostDueAutomation(
        { slug, invoiceId: invoice.id },
        {
          is_automatic_soft_reminder_enabled: softReminderEnabled,
          is_automatic_debt_collection_enabled: debtCollectionEnabled
        }
      ),
    onSuccess: () => {
      toast.success(t('general.changesSaved'))
      invalidateInvoiceQueries()
      handleOnClose()
    },
    onError: () => {
      toast.error(t('error.genericRetry'))
    }
  })

  return (
    <Prompt
      title={t('invoicing.details.postDueAutomation.prompt.title')}
      description={
        <StyledContent>
          <StyledHelp>{t('invoicing.details.postDueAutomation.prompt.description')}</StyledHelp>
          <Alert
            type="warning"
            isVisible={cannotUsePostDueAutomation}
            marginBottom={0}
            description={t(
              showKraviaCheckbox
                ? 'invoicing.details.postDueAutomation.prompt.debtCollectionRecurrenceAlert'
                : 'invoicing.details.postDueAutomation.prompt.softReminderRecurrenceAlert'
            )}
          />
          <StyledCheckboxGroup>
            <Checkbox
              checked={softReminderEnabled}
              onChange={event => setSoftReminderEnabled(event.target.checked)}
              label={
                <StyledCheckboxLabel>
                  {t('invoicing.details.postDueAutomation.prompt.softReminderLabel')}
                  <TextTooltip
                    tooltip={t('invoicing.details.postDueAutomation.prompt.softReminderInfo')}
                  >
                    <FaQuestionCircle />
                  </TextTooltip>
                </StyledCheckboxLabel>
              }
            />
            {showKraviaCheckbox && (
              <Checkbox
                checked={debtCollectionEnabled}
                onChange={event => setDebtCollectionEnabled(event.target.checked)}
                label={
                  <StyledCheckboxLabel>
                    {t('invoicing.details.postDueAutomation.prompt.debtCollectionLabel')}
                    <TextTooltip
                      tooltip={t('invoicing.details.postDueAutomation.prompt.debtCollectionInfo')}
                    >
                      <FaQuestionCircle />
                    </TextTooltip>
                  </StyledCheckboxLabel>
                }
              />
            )}
          </StyledCheckboxGroup>
        </StyledContent>
      }
      isVisible={showPrompt}
      onClose={handleOnClose}
      buttons={[
        {
          text: t('general.cancel'),
          action: handleOnClose
        },
        {
          text: t('general.save'),
          action: () => updateMutation.mutate(),
          icon: <FaSave />,
          intent: 'primary',
          disabled: updateMutation.isPending,
          showSpinner: updateMutation.isPending
        }
      ]}
    />
  )
}

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const StyledHelp = styled.p`
  margin: 0;
`

const StyledCheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const StyledCheckboxLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

export default InvoicePostDueAutomationSettings
